import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../shared/ModalShell';
import { usePatientCard } from '../../hooks/usePatientCard';
import type { PatientMonitoringBlock } from '../../services/patientCard';
import type { OpenVirtualAppointmentsNowResult } from '../../services/virtualAppointments';
import type { OpenNowPatientContext } from './openNowDraft';
import { OpenNowFooter } from './OpenNowFooter';
import { OpenVirtualAppointmentsNowForm } from './OpenVirtualAppointmentsNowForm';
import { useOpenVirtualAppointmentsNow } from './useOpenVirtualAppointmentsNow';
import { usePatientOpenVirtualAppointments } from './usePatientOpenVirtualAppointments';
import {
    VirtualAppointmentLoadError,
    VirtualAppointmentSplitSkeleton
} from './VirtualAppointmentSplitStates';

const TITLE_ID = 'va-open-now-title';

interface OpenVirtualAppointmentsNowModalProps {
    patientId: string;
    /** Lets a patient with VAs already open move into the VA step without new ones. */
    allowMoveOnly: boolean;
    /** False outside Tx-0 to Tx-3, where new VAs can only be planned; see openNowScopeFor. */
    canOpenNow: boolean;
    onClose: () => void;
    onOpened: (result: OpenVirtualAppointmentsNowResult) => void;
}

// Once loaded, the form stays up through background refetches so a failed one never drops drafts.
function useLoadedOnce(ready: boolean): boolean {
    const [loaded, setLoaded] = useState(ready);
    useEffect(() => {
        if (ready) setLoaded(true);
    }, [ready]);
    return loaded || ready;
}

function patientContext(
    monitoring: PatientMonitoringBlock | undefined,
    canOpenNow: boolean
): OpenNowPatientContext {
    return {
        canOpenNow,
        treatmentStartDate: monitoring?.treatmentStartDate ?? null,
        dmLinked: monitoring?.dmId != null,
        aligners: {
            current: monitoring?.currentAligner ?? null,
            total: monitoring?.totalAligners ?? null
        }
    };
}

// "Add VAs": an open-now VA moves the patient into the VA step; a planned one waits for its trigger.
export function OpenVirtualAppointmentsNowModal({
    patientId,
    allowMoveOnly,
    canOpenNow,
    onClose,
    onOpened
}: OpenVirtualAppointmentsNowModalProps) {
    const { data: card } = usePatientCard(patientId);
    const vas = usePatientOpenVirtualAppointments(patientId, null, {
        fresh: true,
        treatmentStartDate: card?.monitoring?.treatmentStartDate ?? null
    });
    const submitter = useOpenVirtualAppointmentsNow(patientId);
    const { submitting } = submitter;
    const loaded = useLoadedOnce(vas.status === 'ready');

    return (
        <ModalShell
            widthClassName="max-w-[1040px]"
            dismissible={!submitting}
            onDismiss={onClose}
            labelledBy={TITLE_ID}
            panelClassName="rounded-xl flex flex-col h-[min(800px,90vh)] overflow-hidden"
        >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900">
                    Add VAs
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-800 disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>
            {loaded ? (
                <OpenVirtualAppointmentsNowForm
                    patientId={patientId}
                    allowMoveOnly={allowMoveOnly}
                    openVas={vas.open}
                    planned={vas.planned}
                    patient={patientContext(card?.monitoring, canOpenNow)}
                    submitter={submitter}
                    onClose={onClose}
                    onOpened={onOpened}
                />
            ) : (
                <>
                    {vas.status === 'error' ? (
                        <VirtualAppointmentLoadError
                            message="Couldn't load this patient's VAs."
                            onRetry={vas.retry}
                        />
                    ) : (
                        <VirtualAppointmentSplitSkeleton />
                    )}
                    <OpenNowFooter onCancel={onClose} cancelDisabled={false} />
                </>
            )}
        </ModalShell>
    );
}
